/**
 * Tipos de dominio para el módulo de Cuentas del Backoffice Operativo.
 *
 * @taskId T473
 * @see functional/Specs/specs_cycle_4.md HU-27-FE
 */

// ─── Estados ────────────────────────────────────────────────────────────────

export type AccountStatus = 'ACTIVE' | 'SUSPENDED' | 'BLOCKED' | 'DEACTIVATED' | 'CLOSED';
export type TransactionStatus = 'COMPLETED' | 'PENDING' | 'FAILED' | 'CANCELLED';
export type TransactionType = 'CREDIT' | 'DEBIT' | 'TRANSFER_IN' | 'TRANSFER_OUT' | 'DEBIN' | 'FEE';

// ─── Entidades principales ──────────────────────────────────────────────────

/**
 * Cuenta de pago (CVU).
 */
export interface Account {
  id: string;
  /** CVU de 22 dígitos */
  cvu: string;
  alias: string;
  merchantId: string;
  merchantRazonSocial: string;
  /** BigDecimal como string (P-02) */
  saldo: string;
  moneda: 'ARS';
  estado: AccountStatus;
  tipo: 'PAYMENT' | 'SETTLEMENT';
  /** ISO 8601 */
  createdAt: string;
  /** ISO 8601 */
  updatedAt: string;
}

/**
 * Resumen de cuenta para listado.
 */
export interface AccountSummary {
  id: string;
  cvu: string;
  alias: string;
  merchantId: string;
  merchantRazonSocial: string;
  saldo: string;
  moneda: 'ARS';
  estado: AccountStatus;
  tipo: 'PAYMENT' | 'SETTLEMENT';
  createdAt: string;
}

/**
 * Detalle de cuenta con información adicional.
 */
export interface AccountDetail extends Account {
  /** Últimos 10 movimientos */
  recentTransactions: TransactionSummary[];
  /** Datos del usuario titular */
  titular?: {
    id: string;
    nombre: string;
    cuit: string;
    email?: string;
  };
}

/**
 * Transacción resumida para listados.
 */
export interface TransactionSummary {
  id: string;
  fecha: string;
  tipo: TransactionType;
  /** BigDecimal como string (P-02) */
  monto: string;
  moneda: 'ARS';
  estado: TransactionStatus;
  referencia?: string;
}

/**
 * Detalle completo de transacción.
 */
export interface TransactionDetail extends TransactionSummary {
  /** Datos del remitente */
  remitente?: {
    nombre: string;
    cvu?: string;
    cbu?: string;
    cuit?: string;
    banco?: string;
  };
  /** Datos del destinatario */
  destinatario?: {
    nombre: string;
    cvu?: string;
    cbu?: string;
    cuit?: string;
    banco?: string;
  };
  concepto?: string;
  descripcion?: string;
  /** Pista de auditoría */
  auditTrail: TransactionAuditEntry[];
  externalId?: string;
}

/**
 * Entrada de auditoría de transacción.
 */
export interface TransactionAuditEntry {
  timestamp: string;
  evento: string;
  actor?: string;
  detalle?: string;
}

// ─── Request/Response types ─────────────────────────────────────────────────

export interface AccountListParams {
  page?: number;
  size?: number;
  cvu?: string;
  merchantId?: string;
  estado?: AccountStatus;
  fechaDesde?: string;
  fechaHasta?: string;
  search?: string;
}

export interface AccountListResponse {
  content: AccountSummary[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface TransactionHistoryParams {
  accountId: string;
  page?: number;
  size?: number;
  transactionId?: string;
  tipo?: TransactionType;
  estado?: TransactionStatus;
  fechaDesde?: string;
  fechaHasta?: string;
  montoMin?: string;
  montoMax?: string;
}

export interface TransactionHistoryResponse {
  content: TransactionSummary[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface UpdateAccountStatusPayload {
  nuevoEstado: AccountStatus;
  motivo: string;
}

// ─── Transiciones de estado válidas ─────────────────────────────────────────

/**
 * Mapa de transiciones válidas desde cada estado.
 * @see specs_cycle_4.md §HU-27-FE §Transiciones
 */
export const VALID_STATUS_TRANSITIONS: Record<AccountStatus, AccountStatus[]> = {
  ACTIVE: ['SUSPENDED'],
  SUSPENDED: ['ACTIVE'],
  BLOCKED: [], // Solo compliance puede desbloquear
  DEACTIVATED: [],
  CLOSED: [],
};

/**
 * Retorna los estados destino válidos desde el estado actual.
 */
export function getValidTransitions(currentStatus: AccountStatus): AccountStatus[] {
  return VALID_STATUS_TRANSITIONS[currentStatus] || [];
}

/**
 * Verifica si una transición de estado es válida.
 */
export function isValidTransition(from: AccountStatus, to: AccountStatus): boolean {
  return VALID_STATUS_TRANSITIONS[from]?.includes(to) ?? false;
}
